/**
 * PricingManager - Admin component for managing subscription pricing
 * 
 * Allows admins to update prices for team lead subscription tiers
 * and a la carte add-ons. Changes are saved to Firestore and
 * reflected across the app in real-time.
 */

import React, { useState, useEffect } from 'react';
import { usePricing } from '../contexts/PricingContext';
import { useAuth } from '../contexts/AuthContext';
import Button from './shared/Button';
import Card from './shared/Card';
import ConfirmDialog from './shared/ConfirmDialog';
import { useNotification } from '../contexts/NotificationContext';

const PricingManager = () => {
  const { plans, addons, savePricing, loading, lastUpdated, DEFAULT_PLANS, DEFAULT_ADDONS } = usePricing();
  const { userRole } = useAuth();
  const { showSuccess, showError } = useNotification();
  
  const [editedPlans, setEditedPlans] = useState({});
  const [editedAddons, setEditedAddons] = useState({});
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  // Initialize edited state from current pricing
  useEffect(() => {
    if (!loading) {
      setEditedPlans(JSON.parse(JSON.stringify(plans)));
      setEditedAddons(JSON.parse(JSON.stringify(addons)));
    }
  }, [plans, addons, loading]);

  // Check if admin
  if (userRole !== 'admin') {
    return (
      <Card>
        <div className="p-4 text-center">
          <p className="text-red-600">🔒 Admin access required to manage pricing.</p>
        </div>
      </Card>
    );
  }

  const handlePlanChange = (planId, field, value) => {
    setEditedPlans(prev => ({
      ...prev,
      [planId]: {
        ...prev[planId],
        [field]: field.includes('Cost') || field.includes('Limit') || field === 'agentLimit' 
          ? (value === '' ? null : Number(value))
          : value
      }
    }));
    setHasChanges(true);
  };

  const handlePlanFeatureChange = (planId, featureIndex, value) => {
    setEditedPlans(prev => ({
      ...prev,
      [planId]: {
        ...prev[planId],
        features: prev[planId].features.map((f, i) => i === featureIndex ? value : f)
      }
    }));
    setHasChanges(true);
  };

  const handleAddFeature = (planId) => {
    setEditedPlans(prev => ({
      ...prev,
      [planId]: {
        ...prev[planId],
        features: [...(prev[planId].features || []), 'New feature']
      }
    }));
    setHasChanges(true);
  };

  const handleRemoveFeature = (planId, featureIndex) => {
    setEditedPlans(prev => ({
      ...prev,
      [planId]: {
        ...prev[planId],
        features: prev[planId].features.filter((_, i) => i !== featureIndex)
      }
    }));
    setHasChanges(true);
  };

  const handleAddonChange = (addonId, field, value) => {
    setEditedAddons(prev => ({
      ...prev,
      [addonId]: {
        ...prev[addonId],
        [field]: field.includes('Cost') || field === 'price' || field === 'credits'
          ? (value === '' ? 0 : Number(value))
          : value
      }
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    
    // Recalculate derived fields
    const processedAddons = { ...editedAddons };
    Object.keys(processedAddons).forEach(key => {
      const addon = processedAddons[key];
      if (addon.type === 'one_time' && addon.credits && addon.price) {
        addon.pricePerCredit = Number((addon.price / addon.credits).toFixed(2));
      }
    });

    const result = await savePricing(editedPlans, processedAddons);
    
    if (result.success) {
      showSuccess('Pricing updated successfully! Changes are now live across the app.');
      setHasChanges(false);
    } else {
      showError(`Failed to save pricing: ${result.error}`);
    }
    
    setSaving(false);
  };

  const handleReset = () => {
    setEditedPlans(JSON.parse(JSON.stringify(plans)));
    setEditedAddons(JSON.parse(JSON.stringify(addons)));
    setHasChanges(false);
  };

  const handleResetToDefaults = () => {
    setShowResetConfirm(true);
  };

  const confirmResetToDefaults = () => {
    setShowResetConfirm(false);
    setEditedPlans(JSON.parse(JSON.stringify(DEFAULT_PLANS)));
    setEditedAddons(JSON.parse(JSON.stringify(DEFAULT_ADDONS)));
    setHasChanges(true);
  };

  if (loading) {
    return (
      <Card>
        <div className="p-4 text-center">
          <p className="text-gray-500">Loading pricing configuration...</p>
        </div>
      </Card>
    );
  }

  const paidPlanIds = ['starter', 'pro', 'pro_plus'];
  const evalPackIds = ['eval_pack_10', 'eval_pack_20', 'eval_pack_50', 'eval_pack_100'];
  const recurringAddonIds = ['white_label', 'extra_agent'];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">💰 Pricing Management</h2>
          <p className="text-sm text-gray-500 mt-1">
            Manage subscription tiers and add-on pricing for team leads
          </p>
          {lastUpdated && (
            <p className="text-xs text-gray-400 mt-1">
              Last updated: {lastUpdated.toLocaleString()}
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={handleResetToDefaults}
            disabled={saving}
          >
            Reset to Defaults
          </Button>
          {hasChanges && (
            <>
              <Button
                variant="secondary"
                size="sm"
                onClick={handleReset}
                disabled={saving}
              >
                Discard Changes
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleSave}
                loading={saving}
              >
                Save Changes
              </Button>
            </>
          )}
        </div>
      </div>

      {hasChanges && (
        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-sm text-yellow-800">
            ⚠️ You have unsaved changes. Click &quot;Save Changes&quot; to apply them across the app.
          </p>
        </div>
      )}

      {/* Subscription Tiers */}
      <Card>
        <div className="p-4 border-b border-gray-200">
          <h3 className="font-semibold text-gray-900">📊 Subscription Tiers</h3>
          <p className="text-xs text-gray-500">Pricing shown to team leads during signup</p>
        </div>
        <div className="p-4">
          <div className="grid md:grid-cols-3 gap-4">
            {paidPlanIds.map(planId => {
              const plan = editedPlans[planId];
              if (!plan) return null;
              
              return (
                <div key={planId} className="border border-gray-200 rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-gray-900">{plan.name}</h4>
                    {plan.popular && (
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                        Popular
                      </span>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Description</label>
                    <input
                      type="text"
                      value={plan.description || ''}
                      onChange={(e) => handlePlanChange(planId, 'description', e.target.value)}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Monthly ($)</label>
                      <input
                        type="number"
                        value={plan.monthlyCost || ''}
                        onChange={(e) => handlePlanChange(planId, 'monthlyCost', e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                        min="0"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Yearly ($)</label>
                      <input
                        type="number"
                        value={plan.yearlyCost || ''}
                        onChange={(e) => handlePlanChange(planId, 'yearlyCost', e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                        min="0"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Agent Limit</label>
                      <input
                        type="number"
                        value={plan.agentLimit === null ? '' : plan.agentLimit}
                        onChange={(e) => handlePlanChange(planId, 'agentLimit', e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                        placeholder="∞"
                        min="0"
                      />
                      <p className="text-xs text-gray-400 mt-0.5">Empty = unlimited</p>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Eval Limit/mo</label>
                      <input
                        type="number"
                        value={plan.monthlyLimit || ''}
                        onChange={(e) => handlePlanChange(planId, 'monthlyLimit', e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                        min="0"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-xs text-gray-500">Features</label>
                      <button
                        type="button"
                        onClick={() => handleAddFeature(planId)}
                        className="text-xs text-blue-600 hover:text-blue-800"
                      >
                        + Add
                      </button>
                    </div>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {(plan.features || []).map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-1">
                          <input
                            type="text"
                            value={feature}
                            onChange={(e) => handlePlanFeatureChange(planId, idx, e.target.value)}
                            className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded"
                          />
                          <button
                            type="button"
                            onClick={() => handleRemoveFeature(planId, idx)}
                            className="text-red-500 hover:text-red-700 px-1"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
                    <label className="flex items-center gap-1 text-xs text-gray-600">
                      <input
                        type="checkbox"
                        checked={plan.popular || false}
                        onChange={(e) => handlePlanChange(planId, 'popular', e.target.checked)}
                        className="rounded"
                      />
                      Mark as Popular
                    </label>
                    {planId === 'pro_plus' && (
                      <label className="flex items-center gap-1 text-xs text-gray-600">
                        <input
                          type="checkbox"
                          checked={plan.whiteLabelIncluded || false}
                          onChange={(e) => handlePlanChange(planId, 'whiteLabelIncluded', e.target.checked)}
                          className="rounded"
                        />
                        White Label Included
                      </label>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Card>

      {/* Recurring Add-ons */}
      <Card>
        <div className="p-4 border-b border-gray-200">
          <h3 className="font-semibold text-gray-900">🔄 Recurring Add-ons</h3>
          <p className="text-xs text-gray-500">Monthly add-on subscriptions</p>
        </div>
        <div className="p-4">
          <div className="grid md:grid-cols-2 gap-4">
            {recurringAddonIds.map(addonId => {
              const addon = editedAddons[addonId];
              if (!addon) return null;
              
              return (
                <div key={addonId} className="border border-gray-200 rounded-lg p-4 space-y-3">
                  <h4 className="font-medium text-gray-900">{addon.name}</h4>
                  
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Description</label>
                    <input
                      type="text"
                      value={addon.description || ''}
                      onChange={(e) => handleAddonChange(addonId, 'description', e.target.value)}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Monthly Cost ($)</label>
                    <input
                      type="number"
                      value={addon.monthlyCost || ''}
                      onChange={(e) => handleAddonChange(addonId, 'monthlyCost', e.target.value)}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                      min="0"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Card>

      {/* Evaluation Credit Packs */}
      <Card>
        <div className="p-4 border-b border-gray-200">
          <h3 className="font-semibold text-gray-900">📦 Evaluation Credit Packs</h3>
          <p className="text-xs text-gray-500">One-time purchase credit packs</p>
        </div>
        <div className="p-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {evalPackIds.map(addonId => {
              const addon = editedAddons[addonId];
              if (!addon) return null;
              
              const pricePerCredit = addon.credits && addon.price 
                ? (addon.price / addon.credits).toFixed(2) 
                : '0.00';
              
              return (
                <div key={addonId} className="border border-gray-200 rounded-lg p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-gray-900 text-sm">{addon.credits} Credits</h4>
                    {addon.popular && (
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                        Popular
                      </span>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Credits</label>
                      <input
                        type="number"
                        value={addon.credits || ''}
                        onChange={(e) => handleAddonChange(addonId, 'credits', e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                        min="1"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Price ($)</label>
                      <input
                        type="number"
                        value={addon.price || ''}
                        onChange={(e) => handleAddonChange(addonId, 'price', e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                        min="0"
                      />
                    </div>
                  </div>
                  
                  <div className="text-xs text-gray-500">
                    ${pricePerCredit}/credit
                  </div>
                  
                  <label className="flex items-center gap-1 text-xs text-gray-600">
                    <input
                      type="checkbox"
                      checked={addon.popular || false}
                      onChange={(e) => handleAddonChange(addonId, 'popular', e.target.checked)}
                      className="rounded"
                    />
                    Mark as Popular
                  </label>
                </div>
              );
            })}
          </div>
        </div>
      </Card>

      {/* Preview */}
      <Card>
        <div className="p-4 border-b border-gray-200">
          <h3 className="font-semibold text-gray-900">👁️ Preview</h3>
          <p className="text-xs text-gray-500">How pricing will appear to team leads</p>
        </div>
        <div className="p-4">
          <div className="grid md:grid-cols-3 gap-4">
            {paidPlanIds.map(planId => {
              const plan = editedPlans[planId];
              if (!plan) return null;
              
              return (
                <div 
                  key={planId} 
                  className={`border-2 rounded-lg p-4 ${plan.popular ? 'border-blue-400 bg-blue-50' : 'border-gray-200'}`}
                >
                  {plan.popular && (
                    <div className="text-center mb-2">
                      <span className="bg-blue-500 text-white text-xs font-medium px-3 py-1 rounded-full">
                        Most Popular
                      </span>
                    </div>
                  )}
                  <h4 className="text-lg font-bold text-gray-900">{plan.name}</h4>
                  <p className="text-sm text-gray-500 mb-3">{plan.description}</p>
                  <div className="mb-4">
                    <span className="text-3xl font-bold text-gray-900">${plan.monthlyCost}</span>
                    <span className="text-gray-500">/mo</span>
                  </div>
                  <ul className="space-y-1 text-sm text-gray-600">
                    {(plan.features || []).slice(0, 4).map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <span className="text-green-500 mr-2">✓</span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </Card>

      {/* Reset to Defaults Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showResetConfirm}
        onClose={() => setShowResetConfirm(false)}
        onConfirm={confirmResetToDefaults}
        title="Reset to Defaults?"
        message="Reset all pricing to default values? This cannot be undone."
        confirmLabel="Reset"
        confirmVariant="danger"
      />
    </div>
  );
};

export default PricingManager;
